import { useNavigate } from '@tanstack/react-router';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDensity } from '@/components/layout/DensityProvider';

/**
 * The three pieces of app-wide UI state that are not URL state.
 *
 * The command palette, the brief composer and the phone nav sheet all have to be
 * openable from more than one place -- the palette from a keystroke and from the
 * sidebar's search button, the composer from a header button, a keystroke and
 * from the palette itself -- so they live here rather than in whichever view
 * happens to draw a trigger. Everything else that is worth restoring on reload
 * is a route: a task selection, a brief selection, a project.
 */
interface ShellValue {
  paletteOpen: boolean;
  openPalette: () => void;
  closePalette: () => void;
  composeOpen: boolean;
  /** Opens the job composer over whatever page is open. */
  openCompose: () => void;
  closeCompose: () => void;
  navOpen: boolean;
  setNavOpen: (open: boolean) => void;
  /**
   * True while `g` is waiting for its second key. It lives here rather than
   * inside `useShellKeys` because the rail draws it: a chord with no indicator
   * is a mode nobody can tell they are in, and every list shortcut has to stand
   * down while one is armed.
   */
  chordArmed: boolean;
  /** Set by `useShellKeys`, and by nothing else. */
  setChordArmed: (armed: boolean) => void;
}

const ShellContext = createContext<ShellValue | null>(null);

export function ShellProvider({ children }: { children: ReactNode }) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [chordArmed, setChordArmed] = useState(false);

  const closePalette = useCallback(() => setPaletteOpen(false), []);
  const openPalette = useCallback(() => {
    setNavOpen(false);
    setPaletteOpen(true);
  }, []);

  /**
   * Opens the composer over whatever page is open, rather than sending the
   * person to Jobs first and back (issue #89). It used to navigate there
   * because only the Jobs view drew the composer; now it renders once, at the
   * root, as a modal over every page, so filing a job no longer costs the
   * context a person was reading when they decided to.
   */
  const openCompose = useCallback(() => {
    setPaletteOpen(false);
    setNavOpen(false);
    setComposeOpen(true);
  }, []);

  const value = useMemo<ShellValue>(
    () => ({
      paletteOpen,
      openPalette,
      closePalette,
      composeOpen,
      openCompose,
      closeCompose: () => setComposeOpen(false),
      navOpen,
      setNavOpen,
      chordArmed,
      setChordArmed,
    }),
    [paletteOpen, openPalette, closePalette, composeOpen, openCompose, navOpen, chordArmed],
  );

  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
}

export function useShell(): ShellValue {
  const value = useContext(ShellContext);
  if (!value) throw new Error('useShell used outside <ShellProvider>');
  return value;
}

/** True while the cursor is inside a field, so a shortcut must not fire. */
export function isTyping(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  if (!element) return false;
  const tag = element.tagName;
  return (
    tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || element.isContentEditable === true
  );
}

/**
 * The `g` chord's destinations.
 *
 * `d` is the dashboard and `i` is kept beside it, because the front page was
 * called Inbox for the whole life of the server-rendered console and muscle
 * memory does not read a changelog. `f` and `p` are likewise the old names for
 * `/agents` and the People tab; they point at the current routes rather than at
 * the redirects, so the chord does not cost a navigation each time.
 */
const JUMPS: Record<string, string> = {
  d: '/',
  i: '/',
  s: '/tasks',
  t: '/tasks',
  j: '/jobs',
  a: '/agents',
  f: '/agents',
  r: '/review',
  p: '/admin/people',
  m: '/me',
  h: '/about',
};

/**
 * The global keys. `cmd-k` is the one everybody will learn; the rest are the
 * Linear-shaped sequences for people who stop using the mouse: `g` then a letter
 * to jump, `c` to open a job, `d` to change density, `?` for the reference
 * on the About page.
 *
 * Every binding bails out inside a field, and the palette's own keys are handled
 * inside the palette so this listener never has to know it is open.
 */
export function useShellKeys(): void {
  const { openPalette, openCompose, paletteOpen, setChordArmed } = useShell();
  const { toggleDensity } = useDensity();
  const navigate = useNavigate();
  const pending = useRef<{ key: string; at: number } | null>(null);
  const expiry = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // The chord's own life: armed on `g`, spent on the next key, and expired by
    // the clock so the chip in the rail cannot outlive the mode it announces.
    const disarm = () => {
      pending.current = null;
      if (expiry.current) clearTimeout(expiry.current);
      expiry.current = null;
      setChordArmed(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        openPalette();
        return;
      }
      if (paletteOpen || isTyping(event.target) || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }

      const key = event.key.toLowerCase();

      // `g` arms a jump for a second and a half; `g t` is Tasks.
      if (pending.current && Date.now() - pending.current.at < 1500) {
        const to = JUMPS[key];
        disarm();
        if (to) {
          event.preventDefault();
          void navigate({ to });
        }
        return;
      }
      disarm();

      if (key === 'g') {
        pending.current = { key, at: Date.now() };
        setChordArmed(true);
        expiry.current = setTimeout(disarm, 1500);
        return;
      }
      if (key === 'c') {
        event.preventDefault();
        openCompose();
        return;
      }
      if (key === 'd') {
        event.preventDefault();
        toggleDensity();
        return;
      }
      if (event.key === '?') {
        event.preventDefault();
        void navigate({ to: '/about' });
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      if (expiry.current) clearTimeout(expiry.current);
    };
  }, [navigate, openCompose, openPalette, paletteOpen, setChordArmed, toggleDensity]);
}
